<template>
  <v-card-text class="ma-0 pa-0 px-6">
    <p
      class="subtitle2 font-weight-700 blue-grey--text text--darken-3 ma-0 pa-0 mb-4"
      data-testid="recognitions-text-recognitionsListTitle"
    >
      {{ $t('recognitions.recognition_other') }}
    </p>
    <div
      class="d-flex align-center justify-center"
      style="height: 240px"
      v-if="!isLoading && !recognitions.length"
    >
      <RecognitionListEmptyState
        data-testid="recognitions-component-recognitionListEmptyState"
        :hasActiveFilters="hasActiveFilters"
      />
    </div>
    <div ref="listContainer">
      <div v-if="loading || isLoading">
        <RecognitionCardSkeleton
          v-for="item in ITEMS_PER_PAGE"
          :key="item"
          class="mt-2"
        />
      </div>
      <div v-else>
        <RecognitionCard
          data-testid="recognitions-card-recognitionCard"
          v-for="(recognition, i) in recognitions"
          :key="i"
          :hideActions="hideActions"
          :recognition="recognition"
          :isHighlighted="
            Boolean(highlightedRecognitionId) &&
              highlightedRecognitionId === recognition._id
          "
          @delete="deleteRecognition(recognition.id)"
          @edit="$emit('recognitionEdited')"
        />

        <!--
         This empty div is for triggering an event when the user reaches 
         the end of the page in order to catch in mixpanel the interaction with the page.
        -->
        <div v-intersect="onEndReached"></div>

        <v-pagination
          data-testid="recognitions-pagination-recognitionListPagination"
          v-if="totalPages > 1"
          circle
          class="mt-4"
          total-visible="7"
          :length="totalPages"
          :value="currentPage"
          @input="handlePageChange($event)"
        ></v-pagination>
      </div>
    </div>
  </v-card-text>
</template>

<script>
import { mapGetters } from 'vuex'
import { omit } from 'lodash'

import api from '@/api'

import RecognitionCard from './RecognitionCard.vue'
import RecognitionCardSkeleton from './RecognitionCardSkeleton.vue'
import RecognitionListEmptyState from './RecognitionListEmptyState.vue'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
const ITEMS_PER_PAGE = 10
const HIGHLIGHTED_RECOGNITION_QUERY_PARAM = 'highlightedRecognition'

export default {
  name: 'RecognitionsList',

  mixins: [mixin, trackerMixin],

  components: {
    RecognitionCard,
    RecognitionCardSkeleton,
    RecognitionListEmptyState,
  },

  props: [
    'recognitions',
    'totalPages',
    'currentPage',
    'hideActions',
    'hasActiveFilters',
    'section',
    'isLoading',
  ],

  data() {
    return {
      highlightedRecognitionId: this.getRouteQuery()?.[
        HIGHLIGHTED_RECOGNITION_QUERY_PARAM
      ],
      ITEMS_PER_PAGE,
      endReachedPristine: true,
      loading: false,
    }
  },

  watch: {
    recognitions: {
      handler() {
        this.scrollToHighlightedRecognition()
      },
      deep: true,
    },
  },

  computed: {
    ...mapGetters(['organizationId']),
    isHighlightedRecognition() {
      return this.recognitions.some(
        recognition => recognition._id === this.highlightedRecognitionId
      )
    },
  },

  methods: {
    onEndReached(data) {
      if (data[0]?.isIntersecting && this.endReachedPristine) {
        this.endReachedPristine = false
        this.trackRecognitionsNavigate({
          scroll: 'endReached',
        })
      }
    },

    async deleteRecognition(recognitionId) {
      try {
        this.loading = true
        await api.recognition.delete(this.organizationId, recognitionId)
        this.$track('recognitions.Delete', this.$store, { status: 'success' })
      } catch (err) {
        this.$track('recognitions.Delete', this.$store, { status: 'fail' })
        this.handleRequestError(err)
      } finally {
        this.$emit('recognitionDeleted')
        this.loading = false
      }
    },

    async fetchHighlightRecognition() {
      try {
        this.loading = true
        const response = await api.recognition.getById(
          this.organizationId,
          this.highlightedRecognitionId
        )
        if (this.isHighlightedRecognition) return
        this.recognitions.unshift(response.data)
        return response.data
      } catch (err) {
        return false
      } finally {
        this.loading = false
      }
    },

    scrollToRecognition(recognitionId) {
      const cardElement = this.$refs.listContainer?.querySelector(
        `[data-recognition-id='${recognitionId}']`
      )
      if (!cardElement) return

      this.$vuetify.goTo(cardElement)

      this.$refs.listContainer.scrollTo({
        top: cardElement.offsetTop,
        behavior: 'smooth',
      })
    },

    async scrollToHighlightedRecognition() {
      if (!this.highlightedRecognitionId) return
      if (!this.isHighlightedRecognition) await this.fetchHighlightRecognition()
      this.scrollToRecognition(this.highlightedRecognitionId)
      this.replaceRoute({
        query: omit(this.getRouteQuery(), HIGHLIGHTED_RECOGNITION_QUERY_PARAM),
      })
    },

    handlePageChange(data) {
      this.trackRecognitionsNavigate({
        pagination: data,
        sections: this.section,
      })
      this.$emit('pageChange', data)
    },
  },
}
</script>
